import React, { useEffect, useState } from 'react';
import { apiKeysService, ApiKey, ApiScope } from '../services/api';

const statusBadge = (status: string) => {
  const cls = status === 'active' ? 'bg-success' : status === 'revoked' ? 'bg-danger' : 'bg-warning text-dark';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const AdminApiKeysPage: React.FC = () => {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [scopes, setScopes] = useState<ApiScope[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // criar
  const [showModal, setShowModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [newScopes, setNewScopes] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // key gerada (mostrada uma vez)
  const [generatedKey, setGeneratedKey] = useState<ApiKey | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [keysResp, scopesResp] = await Promise.all([
        apiKeysService.list(),
        apiKeysService.getScopes(),
      ]);
      setKeys(keysResp.results);
      setScopes(scopesResp);
    } catch {
      setError('Erro ao carregar as chaves.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleScope = (id: string) => {
    setNewScopes((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleCreate = async () => {
    setCreateError(null);
    if (!newName.trim()) {
      setCreateError('Indica um nome.');
      return;
    }
    if (newScopes.length === 0) {
      setCreateError('Escolhe pelo menos um serviço.');
      return;
    }
    setCreating(true);
    try {
      const created = await apiKeysService.create(newName.trim(), newScopes);
      setShowModal(false);
      setNewName('');
      setNewScopes([]);
      setGeneratedKey(created);
      setCopied(false);
      load();
    } catch (err: any) {
      setCreateError(err?.response?.data?.detail || 'Erro ao criar a chave.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (key: ApiKey) => {
    if (!window.confirm(`Revogar a chave "${key.name}"? Esta acção é irreversível.`)) return;
    try {
      await apiKeysService.revoke(key.id);
      load();
    } catch {
      alert('Erro ao revogar a chave.');
    }
  };

  const copyKey = async () => {
    if (generatedKey?.key) {
      await navigator.clipboard.writeText(generatedKey.key);
      setCopied(true);
    }
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0">
          <i className="bi bi-key-fill me-2"></i>Chaves de API
        </h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          + Nova chave
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="table-responsive">
        <table className="table table-striped table-sm table-hover align-middle">
          <thead className="table-dark">
            <tr>
              <th>Nome</th>
              <th>Prefixo</th>
              <th>Serviços</th>
              <th>Estado</th>
              <th>Criada</th>
              <th>Último uso</th>
              <th>Expira</th>
              <th>Acções</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center text-muted py-3">A carregar…</td></tr>
            )}
            {!loading && keys.length === 0 && (
              <tr><td colSpan={8} className="text-center text-muted py-3">Sem chaves.</td></tr>
            )}
            {keys.map((k) => (
              <tr key={k.id}>
                <td className="fw-semibold">{k.name}</td>
                <td><code>{k.prefix}…</code></td>
                <td>{k.scopes.map((s) => <span key={s} className="badge bg-info text-dark me-1">{s}</span>)}</td>
                <td>{statusBadge(k.status)}</td>
                <td className="small text-muted">{fmtDate(k.created_at)}</td>
                <td className="small text-muted">{fmtDate(k.last_used_at)}</td>
                <td className="small text-muted">{fmtDate(k.expires_at)}</td>
                <td>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    disabled={k.status !== 'active'}
                    onClick={() => handleRevoke(k)}
                  >
                    Revogar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal criar */}
      {showModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowModal(false)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Nova chave de API</h5>
                <button className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                {createError && <div className="alert alert-danger py-2">{createError}</div>}
                <div className="mb-3">
                  <label className="form-label">Nome</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="ex.: Parceiro X — produção"
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">Serviços</label>
                  {scopes.map((s) => (
                    <div className="form-check" key={s.id}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`scope-${s.id}`}
                        checked={newScopes.includes(s.id)}
                        onChange={() => toggleScope(s.id)}
                      />
                      <label className="form-check-label" htmlFor={`scope-${s.id}`}>
                        <code>{s.id}</code> — {s.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
                  {creating ? 'A criar…' : 'Criar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal key gerada (uma vez) */}
      {generatedKey?.key && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Chave criada</h5>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning py-2">
                  <strong>Copia agora.</strong> Esta chave não volta a ser mostrada.
                </div>
                <div className="input-group">
                  <input type="text" className="form-control font-monospace" readOnly value={generatedKey.key} />
                  <button className="btn btn-outline-primary" onClick={copyKey}>
                    {copied ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>
                <p className="small text-muted mt-2 mb-0">
                  Uso: <code>Authorization: Api-Key {generatedKey.key.slice(0, 12)}…</code>
                </p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => setGeneratedKey(null)}>Fechar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApiKeysPage;
